import _ from "lodash";

import { LogicProposalComponent } from "lib/frontend/cm/utils/types";
import { annualizeCashValue } from "lib/shared/compensation";
import {
  getProposedIncomeFrequencyAndEmploymentSubType,
  getUserSalaryForInCycleCalculations,
} from "lib/shared/compensation/pay-frequency";
import { getConvertedCurrency } from "lib/shared/currency/getConvertedCurrency";
import { Proposal as PrismaProposal, Review, SalaryIncomeFrequency } from "prisma/cm/client";

import { isValidProposedNumberValue } from "./common";
import { Proposal } from "./types";
import { EmploymentSubType } from "../compensation/employment/types";
import { getIsOTEReview } from "../review";
import { ReviewExchangeRates } from "../review/types";

export function hasEmptySalaryValue(proposal: PrismaProposal, review: Review) {
  const proposedRaise = getProposedRaise(proposal);
  return (
    review.enableSalary && proposal.eligibleForSalary && !isValidProposedNumberValue(proposedRaise)
  );
}

export function getProposalSalaryIncreaseKey(isOTE: boolean) {
  return isOTE ? "oteIncrease" : "raise";
}

export function getSalaryEngineResults(proposal: Proposal) {
  // @ts-expect-error TS7006
  return proposal.logicSuggestedValues?.[LogicProposalComponent.SALARY]?.engineResults;
}

export function getLogicComputedRaise(proposal: Proposal) {
  const engineResults = getSalaryEngineResults(proposal);
  return engineResults?.reviewSuggestion ?? null;
}

export function getRaiseOverride(proposal: Proposal) {
  return proposal.suggestedRaise ?? null;
}

export function getSuggestedRaise(proposal: Proposal) {
  const override = getRaiseOverride(proposal);
  if (override !== null) {
    return override;
  }

  return getLogicComputedRaise(proposal) ?? null;
}

export function getSuggestedRaiseNote(proposal: Proposal): string[] | null {
  if (proposal.suggestedRaiseNote) {
    return [proposal.suggestedRaiseNote];
  }

  const engineResults = getSalaryEngineResults(proposal);
  return engineResults?.suggestedNotes ?? null;
}

export function getSuggestedOTEIncrease(proposal: Proposal) {
  const engineResults = getSalaryEngineResults(proposal);

  const reviewSuggestion =
    _.get(engineResults, "reviewSuggestionOverride") || _.get(engineResults, "reviewSuggestion");

  return reviewSuggestion ?? null;
}

export function getProposalSalaryIncreaseKeyForReview(review: Review) {
  return getProposalSalaryIncreaseKey(getIsOTEReview(review));
}

export function getProposalDbFieldToAutofill(component: string) {
  // @ts-expect-error TS2339
  const proposalDbFieldToAutofill: keyof Proposal = component === "salary" ? "raise" : component;
  return proposalDbFieldToAutofill;
}

export function getInCycleSumSalary(
  proposals: Proposal[],
  {
    targetCurrency,
    exchangeRates,
  }: {
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
  },
) {
  return _.sumBy(proposals, (p) => {
    const user = p.owner;

    const salary = getUserSalaryForInCycleCalculations({ proposalWithOwner: p });

    if (salary === null) return 0;

    const converted = getConvertedCurrency({
      value: salary,
      options: {
        sourceCurrency: user.salaryCurrencyCode,
        targetCurrency,
        exchangeRates,
      },
    });

    const { proposedSalaryIncomeFrequency, proposedEmploymentSubType } =
      getProposedIncomeFrequencyAndEmploymentSubType({ proposalWithOwner: p });

    const annualized = annualizeCashValue({
      value: converted.raw,
      incomeFrequency: proposedSalaryIncomeFrequency,
      employmentSubType: proposedEmploymentSubType,
    });

    return _.round(annualized, 2);
  });
}

export function getSumSuggestedRaise(
  proposals: Proposal[],
  {
    targetCurrency,
    exchangeRates,
  }: {
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
  },
) {
  return _.sumBy(proposals, (p) => {
    const suggested = getSuggestedRaise(p);

    if (_.isNil(suggested)) {
      return 0;
    }

    const { proposedSalaryIncomeFrequency, proposedEmploymentSubType } =
      getProposedIncomeFrequencyAndEmploymentSubType({ proposalWithOwner: p });

    const annualized = annualizeCashValue({
      value: suggested,
      incomeFrequency: proposedSalaryIncomeFrequency,
      employmentSubType: proposedEmploymentSubType,
    });

    const converted = getConvertedCurrency({
      value: annualized,
      options: {
        sourceCurrency: p.owner.salaryCurrencyCode,
        targetCurrency: targetCurrency,
        exchangeRates: exchangeRates,
      },
    });

    return _.round(converted.raw, 2);
  });
}

export function getProposedRaise(proposal: PrismaProposal) {
  return proposal.raise ?? null;
}

export function getSumProposedRaise(
  proposals: Proposal[],
  {
    targetCurrency,
    exchangeRates,
  }: {
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
  },
) {
  return _.sumBy(proposals, (p) => {
    const proposed = getProposedRaise(p);

    if (_.isNil(proposed)) {
      return 0;
    }

    const { proposedSalaryIncomeFrequency, proposedEmploymentSubType } =
      getProposedIncomeFrequencyAndEmploymentSubType({ proposalWithOwner: p });

    const converted = annualizeAndConvertValue({
      value: proposed,
      sourceCurrency: p.owner.salaryCurrencyCode,
      targetCurrency: targetCurrency,
      exchangeRates: exchangeRates,
      incomeFrequency: proposedSalaryIncomeFrequency,
      employmentSubType: proposedEmploymentSubType,
    });

    // round to 2 decimal places
    return _.round(converted.raw, 2);
  });
}

export function annualizeAndConvertValue({
  value,
  sourceCurrency,
  targetCurrency,
  exchangeRates,
  incomeFrequency,
  employmentSubType,
}: {
  value: number;
  sourceCurrency: string;
  targetCurrency: string;
  exchangeRates: ReviewExchangeRates;
  incomeFrequency: SalaryIncomeFrequency;
  employmentSubType: string;
}) {
  const annualized = annualizeCashValue({
    value,
    incomeFrequency,
    employmentSubType: employmentSubType as EmploymentSubType,
  });

  const converted = getConvertedCurrency({
    value: annualized,
    options: {
      sourceCurrency,
      targetCurrency,
      exchangeRates,
    },
  });

  return converted;
}

export function hasSalaryBeenManuallyEdited<
  T extends { preventAutoAccept: Proposal["preventAutoAccept"] },
>(proposal: T) {
  return proposal.preventAutoAccept?.["salary"] === true;
}

/**
 * this is labeled as temporary because it reads from logicSuggestedValues, which we want to remove
 */
export function temporaryGetSuggestedSalaryComponent<
  T extends {
    logicSuggestedValues: Proposal["logicSuggestedValues"];
    eligibleForSalary: Proposal["eligibleForSalary"];
  },
>(proposal: T, component: string) {
  if (!proposal.eligibleForSalary) return 0;

  // @ts-expect-error TS2322
  return _.get(proposal?.logicSuggestedValues?.salary?.engineResults, component, null);
}

export function getSuggestedSalaryRange<
  T extends { logicSuggestedValues: Proposal["logicSuggestedValues"] },
>(proposal: T) {
  // @ts-expect-error TS7006
  const engineResults = proposal.logicSuggestedValues?.salary;
  const rangeLow = Number(engineResults?.suggestedRangeLow);
  const rangeHigh = Number(engineResults?.suggestedRangeHigh);

  return [rangeLow, rangeHigh] as [number | null, number | null];
}
